/* ============================================================================
   NEW PDP — client behavior
   ============================================================================
   ATC handler attaches a CLICK handler to the button (not a SUBMIT handler
   to the form). This is how the working Product Explorer section does it,
   and it works identically on desktop and mobile.

   Cart drawer opens by dispatching a full pointer+click event sequence on
   the theme's <a is="toggle-link" aria-controls="mini-cart"> element — this
   is the only reliable way to activate the toggle-link custom element on
   real iOS devices, where synthetic .click() alone does not always work.

   Bundle rails (Direction C):
     - flavorMetaMap tracks which rail each flavor came from (railKey,
       railLabel), keyed by handle. First-encountered tile wins for meta.
     - flavorVariantsMap is deduped by handle so the same flavor in
       multiple rails shares one variant lookup.
     - updateFlavorAvailability soft-disables primary tiles (existing
       auto-size-switch behavior) and hard-hides cross-sell tiles that
       don't ship in the current size. Each .npdp-cross-sell expander
       reflects its rail's visible count via .is-empty + an inline
       "Not available in this size" message and a label swap.
     - renderSlots stamps a small uppercase category pill on filled bundle
       slots so users can confirm what's in their mixed bundle.
     - updateRailPills syncs the "X added" pill on every rail.
     - The ATC payload includes a _bundle_category line-item property when
       a bundle item came from a cross-sell rail.

   Simple PDP mode:
     - When data-pdp-mode="simple", initSection branches to initSimpleSection
       which sets up only size tiles, qty, ATC, purchase options (if shown),
       and the price display. All bundle/flavor/rail code is skipped.

   AJAX flavor switch — below-the-fold fix:
     - The previous implementation captured sibling section IDs ONCE on
       initial load and requested them via ?sections=. Because Shopify
       generates section IDs per-template, a flavor on a different template
       produced an ID mismatch and the below-the-fold sections were silently
       skipped — they only updated on a hard refresh.
     - ajaxFlavorSwitch now fetches the FULL product page, re-derives the
       sibling sections from that fresh document, and matches them to the
       live page by their distinguishing class (section-product-nutrition /
       section-product-details) rather than by stale IDs.
     - reviveScripts re-executes any <script> tags inside injected sections,
       since assigning innerHTML does not run them. This is what a real
       refresh was doing for you.

   AJAX flavor switch — reviews fix:
     - The reviews widget (Stamped et al.) binds to the product it hydrated
       with on initial page load. The AJAX switch swapped the PDP, nutrition,
       and details sections but never touched the reviews section, so the
       ORIGINAL product's reviews stuck around after switching flavors.
     - swapReviewsSection now finds the reviews section in the freshly
       fetched document (which carries the new product's data-product-id /
       data-url binding attributes), swaps it into the live page, and
       re-runs its scripts. reinitReviewWidgets then tells the review
       platform to re-scan the DOM and hydrate against the new product.
     - This also covers Bulk Jar ↔ 14 Pack tile redirects automatically,
       since those route through ajaxFlavorSwitch too.
   ============================================================================ */

(function () {
  'use strict';

  const roots = document.querySelectorAll('[data-new-pdp]');
  if (!roots.length) return;

  /* ── AJAX FLAVOR SWITCH ──────────────────────────
     Only relevant for flavor PDPs. Simple PDPs (merch) don't navigate
     between sibling products this way, so we no-op the AJAX switch when
     the active PDP is simple. */

  const FADE_MS = 150;
  let isFlavorSwitching = false;

  function syncPageBackground(newPdpEl) {
    if (!newPdpEl) return;
    const style = newPdpEl.getAttribute('style') || '';
    const m = style.match(/--npdp-bg:\s*(#[0-9a-fA-F]{3,8})/);
    if (m) document.documentElement.style.setProperty('--npdp-bg', m[1]);
    else   document.documentElement.style.removeProperty('--npdp-bg');
  }

  /* innerHTML does NOT execute <script> tags. Clone each one into a fresh
     element so the browser runs it after injection. Without this, any
     below-the-fold section that relies on its own JS (nutrition tabs,
     accordions, etc.) stays inert until a real page navigation. */
  function reviveScripts(container) {
    if (!container) return;
    container.querySelectorAll('script').forEach(old => {
      const s = document.createElement('script');
      Array.from(old.attributes).forEach(a => s.setAttribute(a.name, a.value));
      s.textContent = old.textContent;
      old.replaceWith(s);
    });
  }

  /* ── REVIEWS SWAP + RE-INIT ──────────────────────
     The reviews widget binds to the product it hydrated with on initial
     load. An AJAX flavor switch swaps the PDP but leaves the widget bound
     to the original product, so the wrong reviews stick. Fix: swap the
     reviews section markup from the freshly fetched document (it carries
     the new product's data-product-id / data-url attributes), then tell
     the review platform to re-scan the DOM and re-hydrate.

     NOTE: REVIEW_SELECTORS and findReviewsTarget are declared further down
     in this file. That's fine — these functions only run on click, long
     after the whole IIFE has evaluated. */

  function findReviewsWidgetIn(doc) {
    for (const sel of REVIEW_SELECTORS) {
      const el = doc.querySelector(sel);
      if (el) return el;
    }
    return null;
  }

  function swapReviewsSection(fullDoc) {
    const newWidget = findReviewsWidgetIn(fullDoc);
    const liveWidget = findReviewsTarget();
    if (!newWidget) {
      console.warn('[New PDP] Reviews: no widget found in fetched document');
      return false;
    }
    if (!liveWidget) {
      console.warn('[New PDP] Reviews: no widget found on live page');
      return false;
    }

    /* Swap the whole wrapping Shopify section so any surrounding markup
       (headings, summary bars) follows the product too. Fall back to the
       widget element itself if it isn't inside a section wrapper. */
    const newSection = newWidget.closest('.shopify-section') || newWidget;
    const liveSection = liveWidget.closest('.shopify-section') || liveWidget;

    /* Replace the WHOLE node, not just its innerHTML. Stamped (and most
       review platforms) attach hydration state and internal flags to the
       container element itself, and their re-init calls skip elements they
       think are already initialized — which is why an innerHTML swap +
       re-init still showed the original product's reviews. Importing a
       virgin node from the fetched document throws all of that state away,
       so the platform treats it exactly like a fresh page load. */
    const fresh = document.importNode(newSection, true);
    liveSection.replaceWith(fresh);
    reviveScripts(fresh);

    console.log(
      '[New PDP] Reviews: swapped section',
      fresh.id || fresh.className,
      '| widget product-id:',
      (findReviewsTarget() && findReviewsTarget().getAttribute('data-product-id')) || '(none)'
    );
    return true;
  }

  function reinitReviewWidgets() {
    /* Stamped — loadDisplayWidgets re-scans the DOM for widget containers
       and hydrates them against their current data-product-id. Run every
       method that exists; they're all idempotent. */
    try {
      if (window.StampedFn) {
        console.log('[New PDP] Reviews: re-initializing Stamped');
        if (typeof window.StampedFn.loadDisplayWidgets === 'function') {
          window.StampedFn.loadDisplayWidgets();
        }
        if (typeof window.StampedFn.loadBadges === 'function') {
          window.StampedFn.loadBadges();
        }
        if (typeof window.StampedFn.reloadUIs === 'function') {
          window.StampedFn.reloadUIs();
        }
      }
    } catch (e) {
      console.warn('[New PDP] Stamped re-init failed:', e);
    }

    /* Other platforms — all silently no-op if not installed. */
    try {
      if (window.jdgm && typeof window.jdgm.init === 'function') window.jdgm.init();
    } catch (e) {}
    try {
      if (window.okeWidgetApi && typeof window.okeWidgetApi.initAllWidgets === 'function') {
        window.okeWidgetApi.initAllWidgets();
      }
    } catch (e) {}
    try {
      if (window.yotpo && typeof window.yotpo.refreshWidgets === 'function') {
        window.yotpo.refreshWidgets();
      } else if (window.yotpoWidgetsContainer && typeof window.yotpoWidgetsContainer.initWidgets === 'function') {
        window.yotpoWidgetsContainer.initWidgets();
      }
    } catch (e) {}

    if (!window.StampedFn && !window.jdgm && !window.okeWidgetApi && !window.yotpo && !window.yotpoWidgetsContainer) {
      console.warn('[New PDP] Reviews: no known review platform global found — check console for which app renders the widget');
    }
  }

  function quickSwap(el, newEl) {
    return new Promise(resolve => {
      el.style.transition = 'opacity ' + FADE_MS + 'ms ease';
      el.style.opacity = '0';
      setTimeout(() => {
        el.innerHTML = newEl.innerHTML;
        Array.from(newEl.attributes).forEach(a => {
          if (a.name !== 'class') el.setAttribute(a.name, a.value);
        });
        el.style.transition = 'none';
        void el.offsetHeight;
        el.style.transition = 'opacity ' + FADE_MS + 'ms ease';
        el.style.opacity = '1';
        setTimeout(() => { el.style.transition = ''; resolve(); }, FADE_MS);
      }, FADE_MS);
    });
  }

  /* Returns the distinguishing class for a sibling section element, so we
     can match a freshly-fetched section to its live counterpart without
     relying on Shopify's per-template section IDs. */
  function siblingSectionClass(sectionEl) {
    if (sectionEl.classList.contains('section-product-nutrition')) {
      return 'section-product-nutrition';
    }
    if (sectionEl.classList.contains('section-product-details')) {
      return 'section-product-details';
    }
    return null;
  }

  async function ajaxFlavorSwitch(productUrl) {
    if (isFlavorSwitching) return false;
    isFlavorSwitching = true;
    try {
      /* Fetch the FULL product page. This is the key change: we read the
         new product's REAL section structure and IDs, instead of trusting
         IDs captured on initial load that may not exist on this flavor's
         template. */
      const fullRes = await fetch(productUrl);
      if (!fullRes.ok) throw new Error(`Fetch failed: ${fullRes.status}`);
      const fullDoc = new DOMParser().parseFromString(await fullRes.text(), 'text/html');

      const newMedia   = fullDoc.querySelector('.new-pdp__media');
      const newDetails = fullDoc.querySelector('.new-pdp__details');
      const curMedia   = document.querySelector('.new-pdp__media');
      const curDetails = document.querySelector('.new-pdp__details');
      if (!newMedia || !newDetails || !curMedia || !curDetails) return false;

      const newPdp  = fullDoc.querySelector('.new-pdp');
      const livePdp = document.querySelector('.new-pdp');
      syncPageBackground(newPdp);
      if (newPdp && livePdp) {
        livePdp.setAttribute('style', newPdp.getAttribute('style') || '');
        Array.from(newPdp.attributes).forEach(a => {
          if (a.name.startsWith('data-')) livePdp.setAttribute(a.name, a.value);
        });
      }

      /* Swap the inline <style> blocks inside the PDP section so per-product
         CSS (any flavor-specific overrides) follows the switch. */
      const newPdpWrapper = fullDoc.querySelector('.shopify-section.section-new-pdp');
      if (newPdpWrapper) {
        const liveWrapper = document.querySelector('.shopify-section.section-new-pdp');
        if (liveWrapper) {
          const oldStyles = liveWrapper.querySelectorAll('style');
          const newStyles = newPdpWrapper.querySelectorAll('style');
          oldStyles.forEach((s, i) => {
            if (newStyles[i]) s.textContent = newStyles[i].textContent;
          });
        }
      }

      await Promise.all([
        quickSwap(curMedia, newMedia),
        quickSwap(curDetails, newDetails)
      ]);

      /* Swap below-the-fold sections by MATCHING ON CLASS, not stale IDs.
         For each sibling section in the freshly fetched document, find the
         live section that carries the same distinguishing class and replace
         its contents, then re-run any scripts it brought along. */
      const newSiblingSections = fullDoc.querySelectorAll(
        '.shopify-section.section-product-nutrition, .shopify-section.section-product-details'
      );
      newSiblingSections.forEach(newSection => {
        const matchClass = siblingSectionClass(newSection);
        if (!matchClass) return;
        const liveSection = document.querySelector('.shopify-section.' + matchClass);
        if (liveSection) {
          liveSection.innerHTML = newSection.innerHTML;
          /* Also sync data-/id attributes in case downstream code reads them */
          Array.from(newSection.attributes).forEach(a => {
            if (a.name === 'id' || a.name.startsWith('data-')) {
              liveSection.setAttribute(a.name, a.value);
            }
          });
          reviveScripts(liveSection);
        }
      });

      /* Swap the reviews section so the platform gets a virgin placeholder
         carrying the NEW product's binding attributes, then re-hydrate.
         Stamped hydrates lazily and on its own schedule, so re-init on the
         next frame plus a few delayed retries — each call is idempotent. */
      const reviewsSwapped = swapReviewsSection(fullDoc);
      requestAnimationFrame(() => reinitReviewWidgets());
      setTimeout(reinitReviewWidgets, 600);
      setTimeout(reinitReviewWidgets, 1600);
      setTimeout(reinitReviewWidgets, 3200);
      if (!reviewsSwapped) {
        console.warn('[New PDP] Reviews section not found in fetched doc — widget will re-init against current DOM');
      }

      history.pushState({ flavorUrl: productUrl }, '', productUrl);
      const t = fullDoc.querySelector('title');
      if (t) document.title = t.textContent;

      const root = document.querySelector('[data-new-pdp]');
      if (root) initSection(root);

      const newSticky = fullDoc.querySelector('product-sticky-form');
      const curSticky = document.querySelector('product-sticky-form');
      if (newSticky && curSticky) curSticky.replaceWith(newSticky);

      document.dispatchEvent(new CustomEvent('cure:flavor-switched', { detail: { url: productUrl } }));

      const top = document.querySelector('.section-new-pdp');
      if (top && top.getBoundingClientRect().top < -100) {
        top.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return true;
    } catch (err) {
      console.error('[New PDP] AJAX flavor switch error:', err);
      return false;
    } finally {
      isFlavorSwitching = false;
    }
  }

  window.addEventListener('popstate', async () => {
    if (isFlavorSwitching) return;
    if (!(await ajaxFlavorSwitch(window.location.pathname))) window.location.reload();
  });

  history.replaceState({ flavorUrl: window.location.pathname }, '', window.location.pathname);

  const initialPdp = document.querySelector('.new-pdp');
  if (initialPdp) syncPageBackground(initialPdp);

 /* ── SIZE REDIRECT (Bulk Jar ↔ 14 Pack) ─────────────
   These tiles redirect to a different product entirely (different SKU
   format). We route through ajaxFlavorSwitch so the transition matches
   the flavor-swap fade. Falls back to a hard navigation if the AJAX
   swap fails for any reason. */
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('[data-bulk-jar-url]');
  if (!btn) return;
  e.preventDefault();
  e.stopImmediatePropagation();
  const url = btn.dataset.bulkJarUrl;
  if (!url) return;

  // Only AJAX-swap when we're on a flavor PDP. Simple PDPs don't go
  // through ajaxFlavorSwitch — fall through to a normal navigation.
  const root = document.querySelector('[data-new-pdp]');
  const isFlavorPdp = root && root.dataset.pdpMode !== 'simple';

  if (isFlavorPdp) {
    const ok = await ajaxFlavorSwitch(url);
    if (ok) return;
  }
  window.location.assign(url);
}, true);

  /* ── STAR RATING — click to scroll to reviews ──────
     Looks for common review section targets in priority order. Some review
     widgets (Stamped especially) lazy-load on scroll, so the widget element
     may not exist in the DOM yet when the user clicks. In that case we
     scroll toward where the placeholder is, then re-target once Stamped
     hydrates. */
  const REVIEW_SELECTORS = [
    '#stamped-main-widget',
    '.stamped-main-widget',
    '#stamped-reviews-widget',
    '[data-widget-type="reviews"]',
    '#reviews',
    '#shopify-product-reviews',
    '#yotpo-main-widget',
    '.yotpo-widget-instance',
    '[data-reviews]',
    '.spr-container',
    '.jdgm-rev-widg',
    '#judgeme_product_reviews',
    '.okendo-reviews-widget',
    '.loox-rating',
    '.r--widget',
    '[id*="review" i]',
    '[class*="reviews-section" i]'
  ];

  function findReviewsTarget() {
    for (const sel of REVIEW_SELECTORS) {
      const el = document.querySelector(sel);
      if (el) return el;
    }
    return null;
  }

  function smoothScrollTo(el) {
    const rect = el.getBoundingClientRect();
    const y = window.pageYOffset + rect.top - 80;
    window.scrollTo({ top: y, behavior: 'smooth' });
  }

  function scrollToReviews() {
    const target = findReviewsTarget();
    if (target) {
      smoothScrollTo(target);
      return;
    }

    // Nothing on the page yet — scroll to the bottom of the PDP so the
    // user lands near where the widget will hydrate, then watch for it.
    // Stamped typically hydrates on first viewport entry, so scrolling
    // toward it will trigger its own initialization.
    const pdp = document.querySelector('.new-pdp');
    if (pdp) {
      const rect = pdp.getBoundingClientRect();
      window.scrollTo({
        top: window.pageYOffset + rect.bottom - 100,
        behavior: 'smooth'
      });
    }

    // Poll briefly for the widget to appear. If it shows up, scroll to it
    // precisely. We give it 4 seconds, which covers slow networks and
    // Stamped's typical hydration delay.
    let attempts = 0;
    const maxAttempts = 40; // 40 * 100ms = 4s
    const poll = setInterval(() => {
      attempts++;
      const found = findReviewsTarget();
      if (found) {
        clearInterval(poll);
        smoothScrollTo(found);
      } else if (attempts >= maxAttempts) {
        clearInterval(poll);
        console.warn('[New PDP] No reviews section found after polling');
      }
    }, 100);
  }

  document.addEventListener('click', (e) => {
    const ratingBtn = e.target.closest('[data-npdp-rating]');
    if (!ratingBtn) return;
    e.preventDefault();
    scrollToReviews();
  });
  /* ── CLINICIANS BADGE — click to scroll to testimonials ──────
     The badge is an <a> whose href points at an on-page anchor (default
     #frontrow-testimonials-section, overridable via the clinicians_link
     section setting). We intercept the click so we get a smooth scroll
     with the same -80px header offset used elsewhere. If the target
     element isn't on the page, we fall back to scrollToReviews(). */
  document.addEventListener('click', (e) => {
    const clinBtn = e.target.closest('[data-npdp-clinicians]');
    if (!clinBtn) return;
    e.preventDefault();

    const href = clinBtn.getAttribute('href') || '';
    let target = null;
    if (href.startsWith('#') && href.length > 1) {
      target = document.getElementById(href.slice(1));
    }

    if (target) {
      smoothScrollTo(target);
    } else {
      // Anchor not present — fall back to the reviews helper.
      scrollToReviews();
    }
  });

  /* ── YOU MIGHT ALSO LIKE — product recommendations ──
     Fetches Shopify's product-recommendations endpoint (the URL is built in
     Liquid with intent=related, which draws from the Search & Discovery app's
     recommendation rules). The cards are injected into [data-npdp-recs-target],
     replacing the skeleton placeholders. The heading is revealed only once
     real cards land, so an empty recommendation set leaves nothing behind.

     Loading is deferred until the element nears the viewport via
     IntersectionObserver — no point fetching recommendations the user may
     never scroll to. Falls back to an immediate fetch if IO is unavailable. */
  function initRecommendations() {
    const rec = document.querySelector('[data-npdp-recs]');
    if (!rec) return;

    const url = rec.dataset.url;
    if (!url) {
      console.warn('[New PDP] Recommendations element has no data-url');
      return;
    }

    let loaded = false;

    async function loadRecs() {
      if (loaded) return;
      loaded = true;

      const target = rec.querySelector('[data-npdp-recs-target]');
      const heading = rec.querySelector('[data-npdp-recs-heading]');

      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error('Recommendations fetch returned ' + res.status);
        const text = await res.text();

        // The endpoint returns a full section document. Parse it and pull
        // out just the cards grid so we don't inject stray wrapper markup.
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const grid = doc.querySelector('.npdp-recs__grid:not(.npdp-recs__grid--skeleton)');

        if (grid && grid.children.length > 0) {
          if (target) {
            target.innerHTML = '';
            target.appendChild(grid);
          }
          if (heading) heading.hidden = false;
          rec.classList.add('is-loaded');
        } else {
          // No recommendations available — remove the whole block so we
          // don't leave an empty heading or skeleton on the page.
          rec.remove();
        }
      } catch (err) {
        console.warn('[New PDP] Could not load recommendations:', err);
        // On failure, quietly remove the block rather than showing a
        // permanent skeleton.
        rec.remove();
      }
    }

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            loadRecs();
            io.disconnect();
          }
        });
      }, { rootMargin: '300px 0px' });
      io.observe(rec);
    } else {
      loadRecs();
    }
  }

  initRecommendations();

  /* ============================================================================
     IMAGE LIGHTBOX — click-to-expand for hero + collage images
     Navigates between all media images with arrow keys, swipe, or buttons.
     ============================================================================ */

  const lightboxState = {
    images: [],
    index: 0,
    el: null
  };

  function buildLightbox() {
    if (lightboxState.el) return lightboxState.el;
    const lb = document.createElement('div');
    lb.className = 'npdp-lightbox';
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-modal', 'true');
    lb.setAttribute('aria-label', 'Image viewer');
    lb.innerHTML =
      '<button type="button" class="npdp-lightbox__btn npdp-lightbox__close" aria-label="Close">' +
        '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
      '</button>' +
      '<button type="button" class="npdp-lightbox__btn npdp-lightbox__prev" aria-label="Previous image">' +
        '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>' +
      '</button>' +
      '<button type="button" class="npdp-lightbox__btn npdp-lightbox__next" aria-label="Next image">' +
        '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>' +
      '</button>' +
      '<div class="npdp-lightbox__stage">' +
        '<div class="npdp-lightbox__frame">' +
          '<img class="npdp-lightbox__img" alt="">' +
        '</div>' +
      '</div>' +
      '<div class="npdp-lightbox__counter" aria-live="polite"></div>';
    document.body.appendChild(lb);
    lightboxState.el = lb;

    lb.querySelector('.npdp-lightbox__close').addEventListener('click', closeLightbox);
    lb.querySelector('.npdp-lightbox__prev').addEventListener('click', () => navLightbox(-1));
    lb.querySelector('.npdp-lightbox__next').addEventListener('click', () => navLightbox(1));
    lb.addEventListener('click', (e) => {
      if (e.target === lb || e.target.classList.contains('npdp-lightbox__stage')) {
        closeLightbox();
      }
    });

    let touchStartX = null;
    let touchStartY = null;
    lb.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 1) return;
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    }, { passive: true });
    lb.addEventListener('touchend', (e) => {
      if (touchStartX === null) return;
      const dx = e.changedTouches[0].clientX - touchStartX;
      const dy = e.changedTouches[0].clientY - touchStartY;
      touchStartX = touchStartY = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
        navLightbox(dx < 0 ? 1 : -1);
      }
    }, { passive: true });

    return lb;
  }

  function getHighResSrc(img) {
    const srcset = img.getAttribute('srcset');
    if (srcset) {
      const candidates = srcset.split(',').map(s => {
        const parts = s.trim().split(/\s+/);
        const w = parts[1] ? parseInt(parts[1], 10) : 0;
        return { url: parts[0], w: w };
      });
      candidates.sort((a, b) => b.w - a.w);
      if (candidates[0]) return candidates[0].url;
    }
    return img.currentSrc || img.src;
  }

  function collectLightboxImages() {
    const imgs = Array.from(document.querySelectorAll(
      '.new-pdp__hero .new-pdp__hero-img, .new-pdp__collage .new-pdp__collage-img'
    ));
    return imgs.map(img => ({
      src: getHighResSrc(img),
      alt: img.alt || ''
    }));
  }

  function getLightboxClickTargets() {
    const targets = [];
    const hero = document.querySelector('.new-pdp__hero');
    if (hero && hero.querySelector('.new-pdp__hero-img')) targets.push(hero);
    document.querySelectorAll(
      '.new-pdp__collage-wide, .new-pdp__collage-half'
    ).forEach(el => {
      if (el.querySelector('.new-pdp__collage-img')) targets.push(el);
    });
    return targets;
  }

  function openLightbox(startIndex) {
    lightboxState.images = collectLightboxImages();
    if (!lightboxState.images.length) return;
    lightboxState.index = Math.max(0, Math.min(startIndex, lightboxState.images.length - 1));
    const lb = buildLightbox();
    renderLightbox();
    lb.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  function closeLightbox() {
    if (!lightboxState.el) return;
    lightboxState.el.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  function navLightbox(delta) {
    const total = lightboxState.images.length;
    if (!total) return;
    lightboxState.index = (lightboxState.index + delta + total) % total;
    renderLightbox();
  }

  function renderLightbox() {
    const lb = lightboxState.el;
    if (!lb) return;
    const img = lb.querySelector('.npdp-lightbox__img');
    const counter = lb.querySelector('.npdp-lightbox__counter');
    const prev = lb.querySelector('.npdp-lightbox__prev');
    const next = lb.querySelector('.npdp-lightbox__next');
    const cur = lightboxState.images[lightboxState.index];
    if (!cur) return;
    img.style.opacity = '0';
    const tmp = new Image();
    tmp.onload = () => {
      img.src = cur.src;
      img.alt = cur.alt;
      img.style.opacity = '1';
    };
    tmp.onerror = () => {
      img.src = cur.src;
      img.alt = cur.alt;
      img.style.opacity = '1';
    };
    tmp.src = cur.src;
    counter.textContent = (lightboxState.index + 1) + ' / ' + lightboxState.images.length;
    const single = lightboxState.images.length <= 1;
    prev.disabled = single;
    next.disabled = single;
  }

  document.addEventListener('click', (e) => {
    const target = e.target.closest('.new-pdp__hero, .new-pdp__collage-wide, .new-pdp__collage-half');
    if (!target) return;
    if (e.target.closest('.npdp-clinicians')) return;
    e.preventDefault();
    const targets = getLightboxClickTargets();
    const idx = targets.indexOf(target);
    openLightbox(idx >= 0 ? idx : 0);
  });

  document.addEventListener('keydown', (e) => {
    if (!lightboxState.el || !lightboxState.el.classList.contains('is-open')) return;
    if (e.key === 'Escape') closeLightbox();
    else if (e.key === 'ArrowLeft') navLightbox(-1);
    else if (e.key === 'ArrowRight') navLightbox(1);
  });

  /* ============================================================================
     CART DRAWER — open via the theme's toggle-link anchor
     ============================================================================ */

  function refreshMiniCartDrawer() {
    const drawerEl = document.querySelector('cart-drawer#mini-cart, #mini-cart, cart-drawer');
    if (!drawerEl) {
      console.warn('[New PDP] No cart-drawer element found to refresh');
      return Promise.resolve();
    }

    return fetch('/?section_id=mini-cart', {
      headers: { 'Accept': 'text/html' }
    })
    .then(res => {
      if (!res.ok) throw new Error('Section render returned ' + res.status);
      return res.text();
    })
    .then(html => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      let newDrawer = doc.querySelector('cart-drawer#mini-cart, #mini-cart');
      if (!newDrawer) newDrawer = doc.querySelector('cart-drawer');

      if (newDrawer) {
        drawerEl.innerHTML = newDrawer.innerHTML;
        console.log('[New PDP] Refreshed cart drawer contents');
      } else {
        console.warn('[New PDP] Section render had no cart-drawer; using whole body');
        drawerEl.innerHTML = doc.body.innerHTML;
      }
    })
    .catch(err => {
      console.warn('[New PDP] Could not refresh cart drawer:', err);
    });
  }

  function isDrawerVisuallyOpen() {
    const drawer = document.querySelector('cart-drawer#mini-cart, cart-drawer, #mini-cart');
    if (!drawer) return false;

    if (drawer.hasAttribute('open')) return true;
    const cls = drawer.className || '';
    if (/\b(is-open|is-active|active|drawer--is-open|open|visible)\b/.test(cls)) {
      return true;
    }

    if (drawer.getAttribute('aria-hidden') === 'false') return true;

    const rect = drawer.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return false;
    const vw = window.innerWidth;
    if (rect.left >= vw) return false;
    if (rect.right <= 0) return false;
    const styles = window.getComputedStyle(drawer);
    if (styles.display === 'none') return false;
    if (styles.visibility === 'hidden') return false;
    if (parseFloat(styles.opacity) === 0) return false;
    if (drawer.getAttribute('aria-hidden') === 'true') return false;

    const trigger = document.querySelector('[aria-controls="mini-cart"]');
    if (trigger && trigger.getAttribute('aria-expanded') === 'true') return true;

    return false;
  }

  function peOpenCartDrawer() {
    const trigger = document.querySelector(
      'a[aria-controls="mini-cart"], [is="toggle-link"][aria-controls="mini-cart"]'
    );

    if (!trigger) {
      console.warn('[New PDP] No toggle-link trigger found, falling back to /cart');
      window.location.href = '/cart';
      return;
    }

    console.log('[New PDP] Opening drawer via toggle-link:', trigger);

    try { trigger.click(); } catch (e) {}

    setTimeout(() => {
      if (isDrawerVisuallyOpen()) {
        console.log('[New PDP] Drawer opened via native click — done');
        return;
      }

      console.log('[New PDP] Native click did not open drawer, dispatching synthetic events');
      trigger.setAttribute('aria-expanded', 'true');

      const fire = (type, Ctor, init) => {
        try {
          const evt = new Ctor(type, Object.assign({
            bubbles: true,
            cancelable: true,
            composed: true,
            view: window
          }, init || {}));
          trigger.dispatchEvent(evt);
        } catch (err) {}
      };

      if (typeof PointerEvent !== 'undefined') {
        fire('pointerdown', PointerEvent, { pointerType: 'touch', isPrimary: true });
      }
      fire('mousedown', MouseEvent);
      if (typeof PointerEvent !== 'undefined') {
        fire('pointerup', PointerEvent, { pointerType: 'touch', isPrimary: true });
      }
      fire('mouseup', MouseEvent);
      fire('click', MouseEvent);

      setTimeout(() => {
        if (isDrawerVisuallyOpen()) {
          console.log('[New PDP] Drawer opened via synthetic events');
          return;
        }
        console.log('[New PDP] Synthetic events did not open drawer, trying element methods');
        const drawer = document.querySelector('cart-drawer#mini-cart, cart-drawer');
        if (drawer) {
          ['open', 'show', 'reveal', 'toggle'].forEach(m => {
            if (typeof drawer[m] === 'function') {
              try { drawer[m](); } catch (e) {}
            }
          });
        }
      }, 200);
    }, 250);
  }

  async function openFlyoutCart() {
    try {
      const cartRes = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (cartRes.ok) {
        const cart = await cartRes.json();
        const events = [
          'cart:change', 'cart:refresh', 'cart:updated',
          'ajaxProduct:added', 'product:added',
          'theme:cart:added', 'theme:cart:change',
          'skio::cart-updated'
        ];
        events.forEach(name => {
          document.dispatchEvent(new CustomEvent(name, {
            bubbles: true,
            detail: { cart: cart }
          }));
        });

        const count = cart.item_count || 0;
        const selectors = [
          '[data-cart-count]', '[data-cart-item-count]',
          '.cart-count', '.cart__count', '.CartCount',
          '.header__cart-count', '.site-header__cart-count',
          '#cart-count', '#CartCount'
        ];
        selectors.forEach(sel => {
          document.querySelectorAll(sel).forEach(el => {
            el.textContent = count;
            if (count > 0) {
              el.classList.remove('hidden', 'is-hidden', 'hide');
              el.removeAttribute('hidden');
            }
          });
        });
      }
    } catch (e) {
      console.warn('[New PDP] cart.js fetch failed:', e);
    }

    await refreshMiniCartDrawer();
    setTimeout(() => peOpenCartDrawer(), 100);
  }

  /* ── INIT ──────────────────────────────────────── */

  roots.forEach(initSection);

  /**
   * Top-level dispatcher: routes to the simple-mode init when this PDP is a
   * one-time product (merch, gift card, etc.) or to the full flavor/bundle
   * init otherwise. The mode is set in Liquid via data-pdp-mode.
   */
  function initSection(root) {
    if (root.dataset.pdpMode === 'simple') {
      initSimpleSection(root);
    } else {
      initFlavorSection(root);
    }
  }

  /* ============================================================================
     SIMPLE PDP INIT — one-time products (merch, accessories, gift card)
     ----------------------------------------------------------------------------
     Wires up the minimal set of controls present on a simple PDP:
       - size/variant tiles (only present if product has >1 variants)
       - purchase options widget (only present if product has selling plans)
       - simple price display (only present if no selling plans)
       - quantity selector
       - ATC button
       - 3rd-party testing modal is NOT rendered in simple mode (no-op)
     ============================================================================ */

  function initSimpleSection(root) {
    const SUBSCRIPTION_DISCOUNT_PCT = 15;

    const $ = (sel) => root.querySelector(sel);
    const $$ = (sel) => Array.from(root.querySelectorAll(sel));

    const state = {
      currentVariantId: null,
      currentVariantPrice: 0,
      currentVariantPriceFormatted: '',
      currentVariantCompareAt: 0,
      defaultPlanId: null,
      selectedPlanId: null,
      nativeVariantSubMap: {},
      isSubSelected: true,
      hasPurchaseOptions: false
    };

    const els = {
      form: root.querySelector('form.npdp-form'),
      variantInput: $('[data-npdp-variant-id]'),
      qtyInput: $('[data-npdp-qty]'),
      sizes: $$('.npdp-size:not([data-bulk-jar-url])'),
      atc: $('[data-npdp-atc]'),
      // Purchase options (only present when product has selling plans)
      purchaseRoot: $('[data-npdp-purchase]'),
      purchaseRows: $$('[data-purchase]'),
      priceSub: $('[data-npdp-price-sub]'),
      priceOtp: $('[data-npdp-price-otp]'),
      compareSub: $('[data-npdp-compare-sub]'),
      compareOtp: $('[data-npdp-compare-otp]'),
      freqSelect: $('[data-npdp-frequency]'),
      freqLabel: $('[data-npdp-freq-label]'),
      subBadge: $('[data-npdp-sub-badge]'),
      skioPriceData: $('[data-npdp-skio-prices]'),
      // Simple price display (only present when no selling plans)
      simplePrice: $('[data-npdp-simple-price]'),
      simpleCurrent: $('[data-npdp-simple-current]'),
      simpleCompare: $('[data-npdp-simple-compare]')
    };

    state.hasPurchaseOptions = !!els.purchaseRoot;

    // Build a variant-id -> compareAtPrice map from inline data on size tiles.
    // For single-variant products there are no size tiles, so we'll skip the
    // compare-at recalculation and just rely on what Liquid rendered.
    const variantCompareMap = {};
    els.sizes.forEach(tile => {
      const id = parseInt(tile.dataset.variantId, 10);
      const ca = parseInt(tile.dataset.variantCompareAt, 10) || 0;
      if (id) variantCompareMap[id] = ca;
    });

    // Set initial variant state from the active size tile if present.
    // For single-variant simple products there's no size tile row rendered,
    // so we fall back to the data-initial-* attributes that Liquid stamps
    // onto the section root. This keeps state.currentVariantPrice (etc.)
    // truthy so updatePriceDisplay doesn't blank out the price with "—".
    const activeSize = root.querySelector('.npdp-size.is-active');
    if (activeSize) {
      state.currentVariantId = parseInt(activeSize.dataset.variantId, 10) || null;
      state.currentVariantPrice = parseInt(activeSize.dataset.variantPrice, 10) || 0;
      state.currentVariantPriceFormatted = activeSize.dataset.variantPriceFormatted || '';
      state.currentVariantCompareAt = variantCompareMap[state.currentVariantId] || 0;
    } else {
      // No size tiles — read from the data-initial-* attributes on the root.
      state.currentVariantId = parseInt(root.dataset.initialVariantId, 10) || null;
      state.currentVariantPrice = parseInt(root.dataset.initialVariantPrice, 10) || 0;
      state.currentVariantPriceFormatted = root.dataset.initialVariantPriceFormatted || '';
      state.currentVariantCompareAt = parseInt(root.dataset.initialVariantCompareAt, 10) || 0;

      // Final fallback to the hidden form input if those somehow aren't set
      if (!state.currentVariantId && els.variantInput) {
        state.currentVariantId = parseInt(els.variantInput.value, 10) || null;
      }
    }

    // Skio plan setup (only if purchase options are present)
    if (els.purchaseRoot && els.purchaseRoot.dataset.skioPlanId) {
      state.defaultPlanId = els.purchaseRoot.dataset.skioPlanId;
      state.selectedPlanId = state.defaultPlanId;
    }

    if (els.skioPriceData) {
      try {
        state.nativeVariantSubMap = JSON.parse(els.skioPriceData.textContent.trim()) || {};
      } catch (e) {
        state.nativeVariantSubMap = {};
      }
    }

    if (els.freqSelect && els.freqSelect.value) {
      state.selectedPlanId = els.freqSelect.value;
    }

    if (els.subBadge) {
      els.subBadge.textContent = `Save ${SUBSCRIPTION_DISCOUNT_PCT}%`;
    }

    function formatMoney(cents) {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, (window.theme && window.theme.moneyFormat) || '${{amount}}');
      }
      return '$' + (cents / 100).toFixed(2);
    }

    function getCurrentProductSubPrice() {
      const variantMap = state.nativeVariantSubMap[String(state.currentVariantId)];
      if (variantMap && typeof variantMap === 'object') {
        if (state.selectedPlanId && variantMap[state.selectedPlanId] != null) {
          return variantMap[state.selectedPlanId];
        }
        if (state.defaultPlanId && variantMap[state.defaultPlanId] != null) {
          return variantMap[state.defaultPlanId];
        }
        for (const planId in variantMap) {
          if (variantMap[planId] != null) return variantMap[planId];
        }
      }
      return Math.floor(state.currentVariantPrice * (1 - SUBSCRIPTION_DISCOUNT_PCT / 100));
    }

    function updatePriceDisplay() {
      // Simple price block (no subscription)
      if (els.simplePrice) {
        if (els.simpleCurrent) {
          els.simpleCurrent.textContent = state.currentVariantPriceFormatted ||
            (state.currentVariantPrice ? formatMoney(state.currentVariantPrice) : '—');
        }
        if (els.simpleCompare) {
          if (state.currentVariantCompareAt > state.currentVariantPrice) {
            els.simpleCompare.textContent = formatMoney(state.currentVariantCompareAt);
          } else {
            els.simpleCompare.textContent = '';
          }
        }
        return;
      }

      // Purchase options block (subscription + one-time)
      const compareCents = state.currentVariantPrice || null;
      const subPriceCents = getCurrentProductSubPrice();

      if (els.priceSub) {
        els.priceSub.textContent = subPriceCents == null
          ? (state.currentVariantPriceFormatted || '—')
          : formatMoney(Math.round(subPriceCents));
      }
      if (els.compareSub) {
        if (compareCents != null && subPriceCents != null && Math.round(subPriceCents) < compareCents) {
          els.compareSub.textContent = formatMoney(compareCents);
        } else {
          els.compareSub.textContent = '';
        }
      }
      if (els.priceOtp) {
        els.priceOtp.textContent = state.currentVariantPrice
          ? formatMoney(state.currentVariantPrice)
          : (state.currentVariantPriceFormatted || '—');
      }
      if (els.compareOtp) {
        els.compareOtp.textContent = '';
      }
    }

    // Variant tile selection (only fires when there are 2+ variants)
    els.sizes.forEach(tile => {
      tile.addEventListener('click', () => {
        els.sizes.forEach(t => {
          t.classList.remove('is-active');
          t.setAttribute('aria-pressed', 'false');
        });
        tile.classList.add('is-active');
        tile.setAttribute('aria-pressed', 'true');

        const vid = tile.dataset.variantId;
        if (els.variantInput) els.variantInput.value = vid;

        state.currentVariantId = parseInt(vid, 10) || null;
        state.currentVariantPrice = parseInt(tile.dataset.variantPrice, 10) || 0;
        state.currentVariantPriceFormatted = tile.dataset.variantPriceFormatted || '';
        state.currentVariantCompareAt = variantCompareMap[state.currentVariantId] || 0;

        updatePriceDisplay();
      });
    });

    // Purchase options (subscribe / one-time) — only wired if present
    function setPurchaseRowUI(type) {
      els.purchaseRows.forEach(row => {
        const active = row.dataset.purchase === type;
        row.classList.toggle('is-active', active);
        row.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    function setPurchase(type) {
      state.isSubSelected = (type === 'subscribe');
      setPurchaseRowUI(type);
      updatePriceDisplay();
    }

    els.purchaseRows.forEach(row => {
      row.addEventListener('click', (e) => {
        if (e.target.closest('[data-npdp-freq-pill]')) return;
        setPurchase(row.dataset.purchase);
      });
    });

    const freqPill = root.querySelector('[data-npdp-freq-pill]');
    if (freqPill) {
      freqPill.addEventListener('click', (e) => {
        e.stopPropagation();
        if (!state.isSubSelected) setPurchase('subscribe');
      });
      freqPill.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (!state.isSubSelected) setPurchase('subscribe');
          if (els.freqSelect) els.freqSelect.focus();
        }
      });
    }

    if (els.freqSelect) {
      els.freqSelect.addEventListener('change', () => {
        state.selectedPlanId = els.freqSelect.value || state.defaultPlanId;
        if (els.freqLabel) {
          const opt = els.freqSelect.options[els.freqSelect.selectedIndex];
          if (opt && opt.textContent.trim()) {
            els.freqLabel.textContent = opt.textContent.trim();
          }
        }
        updatePriceDisplay();
      });
      els.freqSelect.addEventListener('click', (e) => e.stopPropagation());
    }

    function getActiveSellingPlanId() {
      if (!state.hasPurchaseOptions) return null;
      if (!state.isSubSelected) return null;
      if (state.selectedPlanId) return state.selectedPlanId;
      if (state.defaultPlanId) return state.defaultPlanId;
      return null;
    }

    // ATC handler — simple, single-line-item add
    async function handleAtcClick(e) {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (!els.atc || els.atc.disabled) return;

      const variantId = els.variantInput && els.variantInput.value;
      if (!variantId) {
        console.warn('[New PDP simple] No variant selected');
        return;
      }
      const qty = parseInt(els.qtyInput && els.qtyInput.value, 10) || 1;
      const sellingPlan = getActiveSellingPlanId();

      const item = { id: parseInt(variantId, 10), quantity: qty };
      if (sellingPlan) item.selling_plan = parseInt(sellingPlan, 10);

      // Pick up any line-item properties added via @app blocks
      if (els.form) {
        const formData = new FormData(els.form);
        const properties = {};
        for (const [key, val] of formData.entries()) {
          if (key.indexOf('properties[') === 0 && val) {
            const propName = key.replace('properties[', '').replace(']', '');
            // Skip the bundle-mode bookkeeping property; not relevant here.
            if (propName === '_bundle_mode') continue;
            properties[propName] = val;
          }
        }
        if (Object.keys(properties).length) item.properties = properties;
      }

      const payload = { items: [item] };
      console.log('[New PDP simple] Adding to cart:', payload);

      setBusy(true);
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(payload)
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || err.message || 'Unable to add to cart.');
        }
        await res.json();
        showAdded();
        setBusy(false);
        await openFlyoutCart();
      } catch (err) {
        console.error('[New PDP simple] ATC error:', err);
        alert(err.message || 'Something went wrong. Please try again.');
        setBusy(false);
      }
    }

    if (els.atc) {
      els.atc.addEventListener('click', handleAtcClick);
    }
    if (els.form) {
      els.form.addEventListener('submit', (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleAtcClick(e);
      });
    }

    function setBusy(busy) {
      if (!els.atc) return;
      els.atc.disabled = busy;
    }

    function showAdded() {
      if (!els.atc) return;
      if (!els.atc._origHTML) els.atc._origHTML = els.atc.innerHTML;
      els.atc.disabled = false;
      els.atc.innerHTML = 'Added ✓';
      els.atc.classList.add('npdp-atc--added');
      clearTimeout(els.atc._addedTimer);
      els.atc._addedTimer = setTimeout(() => {
        if (els.atc._origHTML) els.atc.innerHTML = els.atc._origHTML;
        els.atc.classList.remove('npdp-atc--added');
      }, 2000);
    }

    updatePriceDisplay();
  }

  /* ============================================================================
     FLAVOR / BUNDLE PDP INIT — original full PDP behavior
     (unchanged from the prior version; renamed from initSection)
     ============================================================================ */

  function initFlavorSection(root) {
    let tiers;
    try { tiers = JSON.parse(root.dataset.bundleTiers); }
    catch (e) { tiers = [{ min: 2, pct: 10 }, { min: 3, pct: 15 }, { min: 4, pct: 20 }]; }
    tiers.sort((a, b) => b.min - a.min);

    const SUBSCRIPTION_DISCOUNT_PCT = 15;

    const state = {
      mode: 'one',
      selectedFlavors: [],
      maxSlots: 4,
      currentHandle: root.dataset.productHandle,
      bundleKey: root.dataset.bundleKey || '_bundle',
      bundleTag: root.dataset.bundleTag || root.dataset.productHandle,
      tiers: tiers,
      currentVariantId: null,
      currentVariantPrice: 0,
      currentVariantTitle: '',
      currentVariantPriceFormatted: '',
      defaultPlanId: null,
      selectedPlanId: null,
      nativeVariantSubMap: {},
      isSubSelected: true
    };

    function currentTier() {
      const count = state.selectedFlavors.length;
      return state.tiers.find(t => count >= t.min) || null;
    }

    const $ = (sel) => root.querySelector(sel);
    const $$ = (sel) => Array.from(root.querySelectorAll(sel));

    const els = {
      form: root.querySelector('form.npdp-form'),
      modeInput: $('[data-npdp-mode-input]'),
      variantInput: $('[data-npdp-variant-id]'),
      qtyInput: $('[data-npdp-qty]'),
      modeOpts: $$('.npdp-mode__opt'),
      bundleWrap: $('[data-npdp-bundle-wrap]'),
      slots: $$('.npdp-slot'),
      flavors: $$('.npdp-flavor'),
      sizes: $$('.npdp-size:not([data-bulk-jar-url])'),
      atc: $('[data-npdp-atc]'),
      atcNote: $('[data-npdp-atc-note]'),
      tierBadge: $('[data-npdp-tier-badge]'),
      progressMsg: $('[data-npdp-progress-msg]'),
      progressFill: $('[data-npdp-progress-fill]'),
      purchaseRoot: $('[data-npdp-purchase]'),
      purchaseRows: $$('[data-purchase]'),
      priceSub: $('[data-npdp-price-sub]'),
      priceOtp: $('[data-npdp-price-otp]'),
      compareSub: $('[data-npdp-compare-sub]'),
      compareOtp: $('[data-npdp-compare-otp]'),
      freqSelect: $('[data-npdp-frequency]'),
      freqLabel: $('[data-npdp-freq-label]'),
      subBadge: $('[data-npdp-sub-badge]'),
      skioPriceData: $('[data-npdp-skio-prices]'),
      rails: $$('.npdp-rail, .npdp-cross-sell'),
      crossSells: $$('.npdp-cross-sell'),
      crossSellToggles: $$('[data-cross-sell-toggle]')
    };

    if (els.purchaseRoot && els.purchaseRoot.dataset.skioPlanId) {
      state.defaultPlanId = els.purchaseRoot.dataset.skioPlanId;
      state.selectedPlanId = state.defaultPlanId;
      console.log('[New PDP] Native Skio default plan ID:', state.defaultPlanId);
    } else {
      console.warn('[New PDP] No native Skio plan ID');
    }

    if (els.skioPriceData) {
      try {
        state.nativeVariantSubMap = JSON.parse(els.skioPriceData.textContent.trim()) || {};
      } catch (e) {
        console.warn('[New PDP] Could not parse Skio variant price map:', e);
        state.nativeVariantSubMap = {};
      }
    }

    if (els.freqSelect && els.freqSelect.value) {
      state.selectedPlanId = els.freqSelect.value;
    }

    const activeSize = root.querySelector('.npdp-size.is-active');
    if (activeSize) {
      state.currentVariantId = parseInt(activeSize.dataset.variantId, 10) || null;
      state.currentVariantPrice = parseInt(activeSize.dataset.variantPrice, 10) || 0;
      state.currentVariantTitle = activeSize.dataset.variantTitle || '';
      state.currentVariantPriceFormatted = activeSize.dataset.variantPriceFormatted || '';
    }

    const flavorVariantsMap = {};
    const flavorMetaMap = {};
    els.flavors.forEach(tile => {
      const handle = tile.dataset.flavorHandle;
      if (!handle) return;
      if (!Object.prototype.hasOwnProperty.call(flavorVariantsMap, handle)) {
        try {
          flavorVariantsMap[handle] = JSON.parse(tile.dataset.variants || '{}');
        } catch (e) {
          console.warn('[New PDP] Could not parse variants for', handle, e);
          flavorVariantsMap[handle] = {};
        }
      }
      if (!Object.prototype.hasOwnProperty.call(flavorMetaMap, handle)) {
        flavorMetaMap[handle] = {
          railKey: tile.dataset.flavorRailKey || 'primary',
          railLabel: tile.dataset.flavorRailLabel || ''
        };
      }
    });

    function updateFlavorAvailability() {
      const sizeTitle = state.currentVariantTitle;

      const railVisibleCount = {};
      els.crossSells.forEach(cs => {
        const k = cs.dataset.railKey;
        if (k) railVisibleCount[k] = 0;
      });

      els.flavors.forEach(tile => {
        const handle = tile.dataset.flavorHandle;
        const railKey = tile.dataset.flavorRailKey || 'primary';
        const fvariants = flavorVariantsMap[handle] || {};
        const matched = fvariants[sizeTitle];
        const hasMatch = !!(matched && matched.available !== false);

        const hasAnySize = Object.keys(fvariants).some(s => {
          const v = fvariants[s];
          return v && v.available !== false;
        });

        if (railKey === 'primary') {
          const trulyUnavailable = !hasAnySize;
          tile.classList.toggle('is-disabled', trulyUnavailable);
          tile.classList.remove('is-hidden');
          tile.setAttribute('aria-disabled', trulyUnavailable ? 'true' : 'false');
          tile.removeAttribute('aria-hidden');
        } else {
          const visible = hasMatch;
          tile.classList.toggle('is-hidden', !visible);
          tile.classList.toggle('is-disabled', !visible);
          tile.setAttribute('aria-disabled', visible ? 'false' : 'true');
          tile.setAttribute('aria-hidden', visible ? 'false' : 'true');

          if (visible) {
            railVisibleCount[railKey] = (railVisibleCount[railKey] || 0) + 1;
          }
        }
      });

      els.crossSells.forEach(cs => {
        const railKey = cs.dataset.railKey;
        const railLabel = cs.dataset.railLabel || '';
        const count = railVisibleCount[railKey] || 0;
        const empty = count === 0;
        cs.classList.toggle('is-empty', empty);

        const emptyMsg = cs.querySelector('.npdp-cross-sell__empty');
        if (emptyMsg) emptyMsg.hidden = !empty;

        const toggle = cs.querySelector('[data-cross-sell-toggle]');
        if (!toggle) return;
        toggle.setAttribute('aria-disabled', empty ? 'true' : 'false');

        const labelEl = toggle.querySelector('[data-cross-sell-label]');
        if (empty && cs.classList.contains('is-expanded')) {
          cs.classList.remove('is-expanded');
          toggle.setAttribute('aria-expanded', 'false');
        }
        if (labelEl) {
          if (empty) {
            labelEl.textContent = 'Not available in this size';
          } else if (cs.classList.contains('is-expanded')) {
            labelEl.textContent = 'Hide ' + railLabel;
          } else {
            labelEl.textContent = 'Add from ' + railLabel;
          }
        }
      });

      const before = state.selectedFlavors.length;
      state.selectedFlavors = state.selectedFlavors.filter(f => {
        const fvariants = flavorVariantsMap[f.handle] || {};
        return !!fvariants[sizeTitle];
      });
      if (state.selectedFlavors.length !== before) {
        renderSlots();
        renderFlavors();
      }
    }

    function getFlavorVariantForCurrentSize(handle) {
      const fvariants = flavorVariantsMap[handle] || {};
      return fvariants[state.currentVariantTitle] || null;
    }

    function getFlavorOtpPrice(handle) {
      const v = getFlavorVariantForCurrentSize(handle);
      return v ? v.price : null;
    }

    function getFlavorSubPrice(handle) {
      const v = getFlavorVariantForCurrentSize(handle);
      if (!v) return null;
      const subPrices = v.subPrices || {};

      if (state.selectedPlanId && subPrices[state.selectedPlanId] != null) {
        return subPrices[state.selectedPlanId];
      }
      if (state.defaultPlanId && subPrices[state.defaultPlanId] != null) {
        return subPrices[state.defaultPlanId];
      }
      for (const planId in subPrices) {
        if (subPrices[planId] != null) return subPrices[planId];
      }
      return Math.floor(v.price * (1 - SUBSCRIPTION_DISCOUNT_PCT / 100));
    }

    function getCurrentProductSubPrice() {
      const variantMap = state.nativeVariantSubMap[String(state.currentVariantId)];
      if (variantMap && typeof variantMap === 'object') {
        if (state.selectedPlanId && variantMap[state.selectedPlanId] != null) {
          return variantMap[state.selectedPlanId];
        }
        if (state.defaultPlanId && variantMap[state.defaultPlanId] != null) {
          return variantMap[state.defaultPlanId];
        }
        for (const planId in variantMap) {
          if (variantMap[planId] != null) return variantMap[planId];
        }
      }
      return Math.floor(state.currentVariantPrice * (1 - SUBSCRIPTION_DISCOUNT_PCT / 100));
    }

    function getOtpSubtotal() {
      if (state.mode === 'bundle') {
        if (state.selectedFlavors.length === 0) return null;
        let total = 0;
        for (const f of state.selectedFlavors) {
          const p = getFlavorOtpPrice(f.handle);
          if (p == null) return null;
          total += p;
        }
        return total;
      }
      return state.currentVariantPrice || null;
    }

    function getSubSubtotal() {
      if (state.mode === 'bundle') {
        if (state.selectedFlavors.length === 0) return null;
        let total = 0;
        for (const f of state.selectedFlavors) {
          const p = getFlavorSubPrice(f.handle);
          if (p == null) return null;
          total += p;
        }
        return total;
      }
      return getCurrentProductSubPrice();
    }

    function calculateDisplayPrice(forceSubState) {
      const isSub = forceSubState !== undefined ? forceSubState : state.isSubSelected;

      if (state.mode === 'bundle') {
        const subtotal = isSub ? getSubSubtotal() : getOtpSubtotal();
        if (subtotal === null) return null;
        const tier = currentTier();
        const bundlePct = tier ? tier.pct : 0;
        return subtotal * (1 - bundlePct / 100);
      }

      return isSub ? getSubSubtotal() : getOtpSubtotal();
    }

    function formatMoney(cents) {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, (window.theme && window.theme.moneyFormat) || '${{amount}}');
      }
      return '$' + (cents / 100).toFixed(2);
    }

    function updatePriceDisplay() {
      let compareCents = null;
      if (state.mode === 'bundle') {
        if (state.selectedFlavors.length > 0) {
          compareCents = 0;
          for (const f of state.selectedFlavors) {
            const p = getFlavorOtpPrice(f.handle);
            if (p == null) { compareCents = null; break; }
            compareCents += p;
          }
        }
      } else {
        compareCents = state.currentVariantPrice || null;
      }

      if (els.priceSub) {
        const subPrice = calculateDisplayPrice(true);
        els.priceSub.textContent = subPrice === null
          ? (state.currentVariantPriceFormatted || '—')
          : formatMoney(Math.round(subPrice));
      }
      if (els.compareSub) {
        const subPrice = calculateDisplayPrice(true);
        if (compareCents != null && subPrice != null && Math.round(subPrice) < compareCents) {
          els.compareSub.textContent = formatMoney(compareCents);
        } else {
          els.compareSub.textContent = '';
        }
      }

      if (els.priceOtp) {
        const otpPrice = calculateDisplayPrice(false);
        els.priceOtp.textContent = otpPrice === null
          ? (state.currentVariantPriceFormatted || '—')
          : formatMoney(Math.round(otpPrice));
      }
      if (els.compareOtp) {
        if (compareCents != null && state.mode === 'bundle') {
          const otpPrice = calculateDisplayPrice(false);
          if (otpPrice != null && Math.round(otpPrice) < compareCents) {
            els.compareOtp.textContent = formatMoney(compareCents);
          } else {
            els.compareOtp.textContent = '';
          }
        } else {
          els.compareOtp.textContent = '';
        }
      }
    }

    els.modeOpts.forEach(btn => btn.addEventListener('click', () => setMode(btn.dataset.mode)));

    function setMode(mode) {
      if (mode !== 'one' && mode !== 'bundle') return;
      state.mode = mode;
      if (els.modeInput) els.modeInput.value = mode;
      els.modeOpts.forEach(b => {
        const active = b.dataset.mode === mode;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-checked', active ? 'true' : 'false');
      });
      root.classList.toggle('is-bundle-mode', mode === 'bundle');
      renderFlavors();
      updateAtcState();
      updatePriceDisplay();
    }

    els.flavors.forEach(tile => {
      tile.addEventListener('click', async (e) => {
        e.preventDefault();

        const handle = tile.dataset.flavorHandle;
        const railKey = tile.dataset.flavorRailKey || 'primary';
        const railLabel = tile.dataset.flavorRailLabel || '';
        const fvariants = flavorVariantsMap[handle] || {};
        const matchedForCurrentSize = fvariants[state.currentVariantTitle];
        const availableInCurrentSize = matchedForCurrentSize && matchedForCurrentSize.available !== false;

        const data = {
          handle: tile.dataset.flavorHandle,
          title: tile.dataset.flavorTitle,
          url: tile.dataset.flavorUrl,
          image: tile.dataset.flavorImage,
          variantId: tile.dataset.variantId,
          railKey: railKey,
          railLabel: railLabel
        };

        if (state.mode === 'one') {
          if (tile.classList.contains('is-disabled')) return;
          if (data.url && data.handle !== state.currentHandle) {
            const ok = await ajaxFlavorSwitch(data.url);
            if (!ok) window.location.href = data.url;
          }
          return;
        }

        if (railKey !== 'primary' && !availableInCurrentSize) return;

        if (railKey === 'primary' && !availableInCurrentSize) {
          const availableSizeTitle = Object.keys(fvariants).find(sizeTitle => {
            const v = fvariants[sizeTitle];
            return v && v.available !== false;
          });
          if (!availableSizeTitle) return;

          const targetSizeTile = els.sizes.find(s => s.dataset.variantTitle === availableSizeTitle);
          if (!targetSizeTile) return;
          targetSizeTile.click();
        }

        if (tile.classList.contains('is-disabled')) return;

       if (state.selectedFlavors.length < state.maxSlots) {
  state.selectedFlavors.push(data);
} else {
  state.selectedFlavors.shift();
  state.selectedFlavors.push(data);
}
        renderSlots();
        renderFlavors();
        updateAtcState();
        updatePriceDisplay();
      });
    });

    els.slots.forEach(slot => {
      slot.addEventListener('click', () => {
        if (state.mode !== 'bundle') return;
        const idx = parseInt(slot.dataset.slotIndex, 10);
        if (!state.selectedFlavors[idx]) return;
        state.selectedFlavors.splice(idx, 1);
        renderSlots();
        renderFlavors();
        updateAtcState();
        updatePriceDisplay();
      });
    });

    els.crossSellToggles.forEach(toggle => {
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        const cs = toggle.closest('.npdp-cross-sell');
        if (!cs) return;
        if (cs.classList.contains('is-empty')) return;

        const expanded = cs.classList.toggle('is-expanded');
        toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');

        const labelEl = toggle.querySelector('[data-cross-sell-label]');
        const railLabel = cs.dataset.railLabel || '';
        if (labelEl) {
          labelEl.textContent = expanded ? ('Hide ' + railLabel) : ('Add from ' + railLabel);
        }
      });
    });

    function renderSlots() {
      els.slots.forEach((slot, i) => {
        const f = state.selectedFlavors[i];
        slot.innerHTML = '';
        if (f) {
          slot.classList.add('is-filled');
          slot.setAttribute('aria-label', `Bundle slot ${i + 1}, ${f.title}`);
          const railTag = f.railLabel
            ? `<span class="npdp-slot__category">${escapeHtml(f.railLabel)}</span>`
            : '';
          slot.innerHTML = `
            <img class="npdp-slot__img" src="${f.image}" alt="${escapeHtml(f.title)}">
            ${railTag}
            <span class="npdp-slot__clear" aria-hidden="true">×</span>
          `;
        } else {
          slot.classList.remove('is-filled');
          slot.setAttribute('aria-label', `Bundle slot ${i + 1}, empty`);
          slot.innerHTML = `
            <span class="npdp-slot__num">${i + 1}</span>
            <span class="npdp-slot__plus" aria-hidden="true">+</span>
            <span class="npdp-slot__placeholder">Add flavor</span>
          `;
        }
      });
    }

    function renderFlavors() {
      els.flavors.forEach(tile => {
        const handle = tile.dataset.flavorHandle;
        if (state.mode === 'bundle') {
          const selected = state.selectedFlavors.some(f => f.handle === handle);
          tile.classList.toggle('is-selected', selected);
          tile.classList.remove('is-current');
          tile.setAttribute('aria-pressed', selected ? 'true' : 'false');
        } else {
          const isCurrent = handle === state.currentHandle;
          tile.classList.toggle('is-current', isCurrent);
          tile.classList.remove('is-selected');
          tile.setAttribute('aria-pressed', isCurrent ? 'true' : 'false');
        }
      });
      updateRailPills();
    }

    function updateRailPills() {
      const primaryPill = root.querySelector(
        '.npdp-rail[data-rail-key="primary"] [data-rail-pill]'
      );
      if (primaryPill) {
        const count = state.selectedFlavors.filter(f => (f.railKey || 'primary') === 'primary').length;
        primaryPill.textContent = count + ' added';
      }

      els.crossSells.forEach(cs => {
        const railKey = cs.dataset.railKey;
        const pill = cs.querySelector('[data-rail-pill]');
        if (!pill || !railKey) return;
        const count = state.selectedFlavors.filter(f => f.railKey === railKey).length;
        pill.textContent = count + ' added';
        pill.hidden = count === 0;
      });
    }

    els.sizes.forEach(tile => {
      tile.addEventListener('click', () => {
        els.sizes.forEach(t => {
          t.classList.remove('is-active');
          t.setAttribute('aria-pressed', 'false');
        });
        tile.classList.add('is-active');
        tile.setAttribute('aria-pressed', 'true');

        const vid = tile.dataset.variantId;
        if (els.variantInput) els.variantInput.value = vid;

        state.currentVariantId = parseInt(vid, 10) || null;
        state.currentVariantPrice = parseInt(tile.dataset.variantPrice, 10) || 0;
        state.currentVariantTitle = tile.dataset.variantTitle || '';
        state.currentVariantPriceFormatted = tile.dataset.variantPriceFormatted || '';

        updateFlavorAvailability();
        updatePriceDisplay();

        document.dispatchEvent(new CustomEvent('npdp:variant-change', {
          detail: { variantId: vid, price: tile.dataset.variantPrice }
        }));
      });
    });

    const tiersAsc = state.tiers.slice().sort((a, b) => a.min - b.min);
    const maxTier = tiersAsc[tiersAsc.length - 1];

    function updateAtcState() {
      const count = state.selectedFlavors.length;
      const invalidBundle = state.mode === 'bundle' && count < 2;
      if (els.atc) els.atc.disabled = invalidBundle;
      if (els.atcNote) els.atcNote.hidden = !invalidBundle;

      const tier = currentTier();
      if (els.tierBadge) {
        els.tierBadge.textContent = tier ? `Save ${tier.pct}%` : `Save ${tiersAsc[0].pct}%`;
      }

      if (els.progressFill) {
        const pct = Math.min((count / maxTier.min) * 100, 100);
        els.progressFill.style.width = pct + '%';
        els.progressFill.dataset.tier = count >= maxTier.min ? 'max' : '';
      }

      if (els.progressMsg) {
        els.progressMsg.innerHTML = getProgressMessage(count);
      }
    }

    function getProgressMessage(count) {
      if (count === 0) return 'Choose at least 2 flavors to add to cart';
      if (count === 1) return 'Add 1 more flavor to unlock <strong>' + tiersAsc[0].pct + '% off</strong>';
      const nextTier = tiersAsc.find(t => t.min > count);
      const currTier = currentTier();
      if (!nextTier) return 'You\'re saving <strong>' + currTier.pct + '%</strong> — max discount unlocked!';
      const needed = nextTier.min - count;
      return 'Saving <strong>' + currTier.pct + '%</strong> — add ' + needed + ' more for <strong>' + nextTier.pct + '% off</strong>';
    }

    function getActiveSellingPlanId() {
      if (!state.isSubSelected) return null;
      if (state.selectedPlanId) return state.selectedPlanId;
      if (state.defaultPlanId) return state.defaultPlanId;

      const skioInput = root.querySelector('[data-npdp-app-blocks] [name="selling_plan"]');
      if (skioInput && skioInput.value && skioInput.value.trim()) {
        return skioInput.value.trim();
      }
      return null;
    }

    function setPurchaseRowUI(type) {
      els.purchaseRows.forEach(row => {
        const active = row.dataset.purchase === type;
        row.classList.toggle('is-active', active);
        row.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    function setPurchase(type) {
      state.isSubSelected = (type === 'subscribe');
      setPurchaseRowUI(type);

      const skioBtn = findSkioButton(type);
      if (skioBtn) {
        if (skioBtn.tagName === 'INPUT') {
          skioBtn.checked = true;
          skioBtn.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          skioBtn.click();
        }
      }

      updatePriceDisplay();
    }

    function findSkioButton(type) {
      const container = root.querySelector('[data-npdp-app-blocks]');
      if (!container) return null;
      if (type === 'subscribe') {
        return container.querySelector(
          '.skio-plan-picker__option--subscription, ' +
          '.skio-plan-picker__option:not(.skio-plan-picker__option--onetime), ' +
          'input[name="selling_plan"]:not([value=""])'
        );
      } else {
        return container.querySelector(
          '.skio-plan-picker__option--onetime, ' +
          'input[name="selling_plan"][value=""]'
        );
      }
    }

    els.purchaseRows.forEach(row => {
      row.addEventListener('click', (e) => {
        if (e.target.closest('[data-npdp-freq-pill]')) return;
        setPurchase(row.dataset.purchase);
      });
    });

    const freqPill = root.querySelector('[data-npdp-freq-pill]');
    if (freqPill) {
      freqPill.addEventListener('click', (e) => {
        e.stopPropagation();
        if (!state.isSubSelected) setPurchase('subscribe');
      });
      freqPill.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (!state.isSubSelected) setPurchase('subscribe');
          if (els.freqSelect) els.freqSelect.focus();
        }
      });
    }

    if (els.freqSelect) {
      els.freqSelect.addEventListener('change', () => {
        state.selectedPlanId = els.freqSelect.value || state.defaultPlanId;

        if (els.freqLabel) {
          const opt = els.freqSelect.options[els.freqSelect.selectedIndex];
          if (opt && opt.textContent.trim()) {
            els.freqLabel.textContent = opt.textContent.trim();
          }
        }

        console.log('[New PDP] Frequency changed -> plan ID:', state.selectedPlanId);
        updatePriceDisplay();
      });
      els.freqSelect.addEventListener('click', (e) => e.stopPropagation());
    }

    if (els.subBadge) {
      els.subBadge.textContent = `Save ${SUBSCRIPTION_DISCOUNT_PCT}%`;
    }

    async function handleAtcClick(e) {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      if (!els.atc || els.atc.disabled) return;

      const qty = parseInt(els.qtyInput && els.qtyInput.value, 10) || 1;
      const sellingPlan = getActiveSellingPlanId();

      console.log('[New PDP] ATC clicked. Mode:', state.mode, 'Subscribe:', state.isSubSelected, 'Plan:', sellingPlan || '(none)');

      if (state.isSubSelected && !sellingPlan) {
        console.warn('[New PDP] Subscribe selected but no selling_plan resolved.');
      }

      let payload;

      if (state.mode === 'bundle') {
        if (state.selectedFlavors.length < 2) {
          alert('Please choose at least 2 flavors to build your bundle.');
          return;
        }
        const tier = currentTier();
        const items = state.selectedFlavors.map(f => {
          const fvariant = getFlavorVariantForCurrentSize(f.handle);
          const itemId = fvariant ? fvariant.id : parseInt(f.variantId, 10);

          const properties = {
            [state.bundleKey]: state.bundleTag,
            '_bundle_flavor': f.title,
            '_bundle_count': String(state.selectedFlavors.length),
            '_bundle_discount': tier ? `${tier.pct}%` : '0%'
          };
          if (f.railKey && f.railKey !== 'primary' && f.railLabel) {
            properties['_bundle_category'] = f.railLabel;
          }

          const item = {
            id: itemId,
            quantity: qty,
            properties: properties
          };
          if (sellingPlan) item.selling_plan = parseInt(sellingPlan, 10);
          return item;
        });
        payload = { items: items };
      } else {
        const variantId = els.variantInput && els.variantInput.value;
        if (!variantId) {
          console.warn('[New PDP] No variant selected');
          return;
        }
        const item = { id: parseInt(variantId, 10), quantity: qty };
        if (sellingPlan) item.selling_plan = parseInt(sellingPlan, 10);

        if (els.form) {
          const formData = new FormData(els.form);
          const properties = {};
          for (const [key, val] of formData.entries()) {
            if (key.indexOf('properties[') === 0 && val) {
              const propName = key.replace('properties[', '').replace(']', '');
              properties[propName] = val;
            }
          }
          if (Object.keys(properties).length) item.properties = properties;
        }

        payload = { items: [item] };
      }

      console.log('[New PDP] Adding to cart:', payload);

      setBusy(true);
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(payload)
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || err.message || 'Unable to add to cart.');
        }
        const data = await res.json();
        console.log('[New PDP] Added successfully:', data);

        if (state.mode === 'bundle') {
          document.dispatchEvent(new CustomEvent('npdp:bundle-added', {
            bubbles: true,
            detail: { items: payload.items, selectedFlavors: state.selectedFlavors.slice() }
          }));
        }

        showAdded();
        setBusy(false);

        await openFlyoutCart();
      } catch (err) {
        console.error('[New PDP] ATC error:', err);
        alert(err.message || 'Something went wrong. Please try again.');
        setBusy(false);
      }
    }

    if (els.atc) {
      els.atc.addEventListener('click', handleAtcClick);
    }

    if (els.form) {
      els.form.addEventListener('submit', (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleAtcClick(e);
      });
    }

    function setBusy(busy) {
      if (!els.atc) return;
      els.atc.disabled = busy;
    }

    function showAdded() {
      if (!els.atc) return;
      if (!els.atc._origHTML) els.atc._origHTML = els.atc.innerHTML;
      els.atc.disabled = false;
      els.atc.innerHTML = 'Added ✓';
      els.atc.classList.add('npdp-atc--added');
      clearTimeout(els.atc._addedTimer);
      els.atc._addedTimer = setTimeout(() => {
        if (els.atc._origHTML) els.atc.innerHTML = els.atc._origHTML;
        els.atc.classList.remove('npdp-atc--added');
      }, 2000);
    }

    function escapeHtml(s) {
      return String(s || '').replace(/[&<>"']/g, c => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
      }[c]));
    }

    /* ── 3RD PARTY TESTING MODAL ─────────────────── */
    const cardBtn = root.querySelector('.thirdparty-card');
    const overlay = root.querySelector('.thirdparty-modal__overlay');
    const closeBtn = overlay && overlay.querySelector('.thirdparty-modal__close');

    if (cardBtn && overlay && closeBtn) {
      cardBtn.addEventListener('click', (e) => {
        e.preventDefault();
        overlay.style.display = 'flex';
        closeBtn.focus();
      });
      closeBtn.addEventListener('click', () => {
        overlay.style.display = 'none';
        cardBtn.focus();
      });
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          overlay.style.display = 'none';
          cardBtn.focus();
        }
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && overlay.style.display === 'flex') {
          overlay.style.display = 'none';
          cardBtn.focus();
        }
      });

      overlay.querySelectorAll('.thirdparty-modal-tab').forEach(tab => {
        tab.addEventListener('click', () => {
          overlay.querySelectorAll('.thirdparty-modal-tab').forEach(t => t.classList.remove('active'));
          overlay.querySelectorAll('.thirdparty-modal-tab-content').forEach(c => c.classList.remove('active'));
          tab.classList.add('active');
          const target = overlay.querySelector('#' + tab.dataset.tab);
          if (target) target.classList.add('active');
        });
      });
    }

    updateFlavorAvailability();
    renderFlavors();
    renderSlots();
    updateAtcState();
    updatePriceDisplay();
  }
})();